import type { DistanceUnit } from 'src/types/market.type';

const KM_TO_MILES = 0.621371;

export function extractPlaceName(rawPlaceName: string | undefined | null): string | undefined {
   if (!rawPlaceName) return undefined;
   const firstSegment = rawPlaceName.split(',')[0]?.trim();
   if (!firstSegment || !isNaN(Number(firstSegment))) return undefined;
   return firstSegment;
}

export function formatDistanceMiles(distanceKm: number): string {
   return (distanceKm * KM_TO_MILES).toFixed(1);
}

/**
 * Sort key for ordering a hotel search result list nearest-first.
 *
 * A hotel the pipeline could not place sorts last rather than first: adaptSerpToListItem collapses
 * a null distance_miles to 0, which is indistinguishable from "on top of the venue" by the time a
 * list component sees it, so sorting the raw value would lead every list with unplaceable hotels.
 */
export function hotelDistanceSortKey(item: any): number {
   const miles = Number(item?.hotel?.distance_miles);
   return Number.isFinite(miles) && miles > 0 ? miles : Infinity;
}

export function haversineDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
   const R = 6371;
   const toRad = (deg: number) => (deg * Math.PI) / 180;
   const dLat = toRad(lat2 - lat1);
   const dLng = toRad(lng2 - lng1);
   const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
   return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function haversineDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
   return haversineDistanceKm(lat1, lng1, lat2, lng2) * KM_TO_MILES;
}

// ─── NPT-2055 — distance units ──────────────────────────────────────────────────────────────
// Hotel APIs keep every distance in MILES. Convert only at display (and at admin input
// boundaries); the US/mi output must stay exactly what it was before markets existed.

export const MILES_TO_KM = 1.609344;

export const milesToKm = (miles: number): number => miles * MILES_TO_KM;

export const kmToMiles = (km: number): number => km / MILES_TO_KM;

/** short: "1.2 mi" / "1.9 km"; long: "1.2 miles" / "1.9 kilometers"; adjective: "5-mile" / "8-kilometer". */
export type DistanceStyle = 'short' | 'long' | 'adjective';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * A distance held in miles, rendered in the market's unit.
 * `digits`: a fixed number of decimals (1 matches every "x.x mi" label), or 'auto', which prints
 * miles exactly as given (whole-number settings like "23 miles") and kilometres to at most one
 * decimal ("37" for 23 mi, "8.5" for 5.3 mi).
 */
export const formatDistance = (
   miles: number,
   unit: DistanceUnit,
   t: Translate,
   { style = 'short', digits = 1 }: { style?: DistanceStyle; digits?: number | 'auto' } = {},
): string => {
   const raw = unit === 'km' ? milesToKm(miles) : miles;
   const value = digits === 'auto' ? (unit === 'km' ? String(Number(raw.toFixed(1))) : String(raw)) : raw.toFixed(digits);
   return t(`distance.${style}.${unit}`, { value });
};
